import { Col, Row } from 'antd';
import _ from 'lodash';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import { ChangeEvent, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'src/components/Button/Button';
import { TextField } from 'src/components/Form/TextField';
import { UploadTransactionsDialog } from 'src/pages/TransactionsPage/components/UploadTransactionsDialog';
import { LoanApplicationsStore } from 'src/stores/LoanApplicationsStore/LoanApplicationsStore';
import {
    TransactionsFilter,
    TransactionsStore,
} from 'src/stores/TransactionsStore/TransactionsStore';
import { UserStore } from 'src/stores/UserStore/UserStore';
import { CssColors } from 'src/styles/CssVariables';
import { downloadFileBinary } from 'src/utils/FileHelper';
import { FormHelper } from 'src/utils/FormHelper';
import styled from 'styled-components';

export const TransactionsHeaderPanel = observer(() => {
    const { t } = useTranslation();

    useEffect(() => {
        return () => {
            LoanApplicationsStore.exportActiveApplicationsLoader.reset();
        };
    }, []);

    const canDownloadActiveApplications = UserStore.can.manage.transactions;

    const handleSearchChange = useCallback(
        _.debounce((event: ChangeEvent<HTMLInputElement>) => {
            const filterSearch = event.target.value;
            if (filterSearch) {
                TransactionsStore.filterCriteria.applyFilter({
                    filterSearch: event.target.value,
                });
            } else {
                TransactionsStore.filterCriteria.resetFilter();
            }
            TransactionsStore.loadList();
        }, DELAY_BEFORE_APPLY_FILTER),
        [],
    );

    return (
        <StyledWrapper>
            <StyledRow gutter={16}>
                <Col flex={1}>
                    <StyledTextField
                        name={formNames.getPath('filterSearch')}
                        inputProps={{
                            onChange: handleSearchChange,
                            allowClear: true,
                            placeholder: t(
                                'App.Components.TransactionFilterPanel.FilterSearchPlaceholder',
                            ),
                        }}
                    />
                </Col>
                <Col>
                    <Button
                        type="primary"
                        onClick={() => {
                            UploadTransactionsDialog.render({
                                onSuccess: () => {
                                    TransactionsStore.loadList();
                                },
                            });
                        }}
                    >
                        {t(
                            'App.Components.TransactionFilterPanel.UploadTransactions',
                        )}
                    </Button>
                </Col>
                {canDownloadActiveApplications && (
                    <Col>
                        <Button
                            onClick={async () => {
                                const file =
                                    await LoanApplicationsStore.exportActiveApplications();

                                const fileName = `active_loan_applications_${moment().format(
                                    'yyyyMMDD_hhmmss',
                                )}`;

                                file && downloadFileBinary(file, fileName);
                            }}
                            loading={
                                LoanApplicationsStore
                                    .exportActiveApplicationsLoader.isLoading
                            }
                        >
                            {t(
                                'App.Components.TransactionFilterPanel.DownloadActiveApplications',
                            )}
                        </Button>
                    </Col>
                )}
            </StyledRow>
        </StyledWrapper>
    );
});

type FilterFormValues = TransactionsFilter;
const formNames = FormHelper.createNameProvider<FilterFormValues>();
const DELAY_BEFORE_APPLY_FILTER = 500;

const StyledWrapper = styled.div`
    display: flex;
    align-items-center;
`;

const StyledRow = styled(Row)`
    width: 100%;
`;

const StyledTextField = styled(TextField)`
    .ant-input-affix-wrapper:hover {
        border-color: ${CssColors.black} !important;
    }
    .ant-input-affix-wrapper:focus,
    .ant-input-affix-wrapper-focused {
        box-shadow: 0 0 0 2px rgba(29, 29, 31, 0.2) !important;
    }

    input.ant-input,
    input.ant-input:focus {
        border: none !important;
        box-shadow: none !important;
    }
`;
